import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import Button from "devextreme-react/button";
import Form, {
  CustomRule,
  FormRef,
  GroupItem,
  Label,
  SimpleItem,
} from "devextreme-react/form";
import { LoadIndicator } from "devextreme-react/load-indicator";
import { call, run, sleep } from "effection";
import sum from "hash-sum";
import { update } from "js-coroutines";
import { useTranslation } from "react-i18next";

import { Case, CaseElse, Switch } from "@app/contexts";
import { encryptPayoad } from "@app/service/helpers";
import {
  appNotify,
  selectEvironmentStored,
  updateSuGroup,
  useAppDispatch,
  useAppSelector,
} from "@app/state";
import { commonStyle } from "@app/style/commonForms";

import "./orgforms.scss";

import type { Operation } from "effection";
import type { ISUEnvironment0, TSWSServerMachine } from "@app/types";
//
//
type TNotesStatus = "none" | "loading" | "success" | "error";

type TDeploymentParams = {
  err: boolean;
  kc: string;
  err_kc?: string;
  msg_kc?: string;
  kcf: string;
  err_kcf?: string;
  msg_kcf?: string;
  kda: string;
  err_kda?: string;
  msg_kda?: string;
  kdm: TSWSServerMachine;
  err_kdm?: string;
  msg_kdm?: string;
  kkm: TSWSServerMachine;
  err_kkm?: string;
  msg_kkm?: string;
  roe: string;
  err_roe?: string;
  msg_roe?: string;
  ses: string;
  err_ses?: string;
  msg_ses?: string;
};

const buildDeploymentParams = (data: ISUEnvironment0): TDeploymentParams => {
  const {
    PERSEED: KC,
    LICENSE_SHORT_NAME: KCF,
    DBAUID: KDA,
    DBSRV_ABR: KDM,
    KIT_SRV: KKM,
  } = data || {};
  const params: TDeploymentParams = {
    err: false,
    kc: KC || "",
    kcf: KCF || "",
    kda: KDA || "",
    kdm: KDM || "",
    kkm: KKM || "",
    roe: "",
    ses: "",
  };
  return params;
};

const DEBOUNCE_MS = 750;

const serverUrl =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_SERVERURL_PROD || ""
    : process.env.REACT_APP_SERVERURL_DEV || "";

function getAliasStrigsFromData(kc: string, kda: string): string[] {
  if (!kc || !kda) return ["", ""];
  const aliasStrings = [`${kc}_${kda}`, `/var/lib/sws/db/${kc}/${kc}.fdb`];
  return aliasStrings;
}

export function GroupEnvironment({ readOnly = false }: { readOnly?: boolean }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const effRef5 = useRef<boolean>(false);
  const formorgenvref = useRef<FormRef>(null);
  const notes_db_ref = useRef<HTMLDivElement>(null);
  const notes_db_status_ref = useRef<HTMLDivElement>(null);
  const notesCrtStatusRef = useRef<TNotesStatus>("none");
  const fetchGuardRef = useRef<boolean>(false);
  const [formDataSource, setFormDataSource] = useState<any>(null);
  const formIsValid = useRef<boolean>(false);
  const currentEnvData = useAppSelector(selectEvironmentStored);

  const dataCrc = useMemo(
    () => sum(currentEnvData),
    [JSON.stringify(currentEnvData)]
  );
  const formDataCrc = useMemo(
    () => sum(formDataSource),
    [JSON.stringify(formDataSource)]
  );

  function paintNotesDb(status: TNotesStatus) {
    return update(function* () {
      if (notes_db_ref.current?.style && notes_db_status_ref.current) {
        notes_db_ref.current.classList.remove("notes-error");
        notes_db_ref.current.classList.remove("notes-success");
        notes_db_ref.current.classList.remove("notes-loading");
        notes_db_status_ref.current.classList.remove("dx-attention");
        notes_db_status_ref.current.classList.remove("dx-valid");
        notes_db_status_ref.current.classList.remove("dx-validation-pending");
        notes_db_status_ref.current.innerHTML = "";
        switch (status) {
          case "none":
            notes_db_ref.current.style.backgroundColor = "#fffcea87";
            break;
          case "loading":
            notes_db_ref.current.classList.add("notes-loading");
            notes_db_status_ref.current.classList.add("dx-validation-pending");
            notes_db_status_ref.current.innerHTML =
              "Se valideaza calea si aliasul";
            break;
          case "success":
            notes_db_ref.current.classList.add("notes-success");
            notes_db_status_ref.current.classList.add("dx-valid");
            notes_db_status_ref.current.innerHTML =
              "Calea catre baza de date si dbs.conf sunt valide";
            break;
          case "error":
            notes_db_ref.current.classList.add("notes-error");
            notes_db_status_ref.current.classList.add("dx-attention");
            notes_db_status_ref.current.innerHTML =
              "Nu se valideaza calea sau aliasul. Vezi si in consola";
            break;
          default:
            yield (notes_db_ref.current.style.backgroundColor = "#fffcea87");
        }
        yield;
      }
    });
  }

  function* eRequestValidation(state: ISUEnvironment0): Operation<boolean> {
    const dbServer = state.DBSRV_ABR as TSWSServerMachine;
    yield* sleep(DEBOUNCE_MS);
    if (!dbServer) return false;
    if (dbServer === "LOCAL") return true;
    if (fetchGuardRef.current) {
      return true;
    }
    fetchGuardRef.current = true;
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.append("Accept", "application/json");
    headers.append("Credentials", "include");
    headers.append("origin", window.location.origin);
    const params = encryptPayoad({
      ...buildDeploymentParams(state),
    });
    const url = `${serverUrl}/rest/api/su/fops/env1`;
    const options = {
      method: "POST",
      headers,
      body: JSON.stringify(params),
    };
    const response = yield* call(fetch(url, options));
    const result = yield* call(response.json()) as Generator<
      any,
      TDeploymentParams
    >;
    if (result.err) {
      console.log("EROARE DE VALIDARE", result);
      dispatch(
        appNotify({
          message: "EROARE DE VALIDARE, pentru detalii consulta consola",
          type: "error",
          duration: 7000,
          position: "bottom",
        })
      );
    }
    const { err } = result || {};
    fetchGuardRef.current = false;
    return Boolean(!err);
  }

  const validateFbDatabaseAndShow = async (newState: ISUEnvironment0) => {
    if (notesCrtStatusRef.current === "loading") return;
    paintNotesDb("loading");
    notesCrtStatusRef.current = "loading";
    try {
      formorgenvref.current?.instance().beginUpdate();
      const isValid = await run(() => eRequestValidation(newState));
      formIsValid.current = isValid;
      paintNotesDb(isValid ? "success" : "error");
      setFormDataSource(newState);
      formorgenvref.current?.instance().validate();
    } finally {
      notesCrtStatusRef.current = formIsValid.current ? "success" : "error";
      formorgenvref.current?.instance().endUpdate();
    }
  };

  function* eUpdateDataset(): Operation<void> {
    const data = structuredClone(formDataSource); // ISUEnvironment0
    try {
      formorgenvref.current?.instance().beginUpdate();
      yield* sleep(1);
    } finally {
      dispatch(updateSuGroup(data));
      formorgenvref.current?.instance().endUpdate();
    }
    return;
  }
  const eLoadDataset = function* (): Operation<void> {
    validateFbDatabaseAndShow({ ...currentEnvData });
    yield* sleep(1);
  };

  useEffect(() => {
    run(eLoadDataset);
  }, [dataCrc]);

  useEffect(() => {
    effRef5.current = true;
    return () => {
      effRef5.current = false;
    };
  });

  const notes_0 = useCallback(
    () => (
      <div className={"notes"}>
        <p>
          Am trecut din cod, ca implicit serverul <strong>APP</strong>, pe care
          ruleaza aceasta aplicatie. In viitor, daca va fi necesar, se poate
          extinde destul de usor pentru a permite si alte servere. Aici vei face
          upload. De aici se serveste clientul.
        </p>
      </div>
    ),
    []
  );
  const notes_1 = useCallback(
    () => (
      <div className={"notes-endline"}>
        <p>{`/var/lib/sws/files/${currentEnvData.PERSEED}`}</p>
      </div>
    ),
    [formDataCrc]
  );

  const kitSrvEditorOptions = useMemo(() => {
    return {
      ...commonStyle,
      readOnly: true,
    };
  }, [dataCrc]);

  const radioDBServerOptions = useMemo(() => {
    return {
      // id:"dbserverRadioGroup",
      // className: "dx-swatch-teamZebra dbserverRadioGroup",
      elementAttr: {
        id: "dbserverRadioGroup",
        className: "dx-swatch-teamZebra dbserverRadioGroup",
      },
      items: [
        { id: "LOCAL", text: "Pe reteaua clientului" },
        { id: "APP", text: "APP / admin.jurnalgold.ro" },
        { id: "DB", text: "DB / db3.jurnalgold.ro" },
        { id: "ONE", text: "ONE / quick.jurnalgold.ro" },
      ],
      value: currentEnvData.DBSRV_ABR,
      valueExpr: "id",
      displayExpr: "text",
      layout: "vertical",
    };
  }, [dataCrc]);

  const notes_db = useCallback(() => {
    const selectedSrv = formorgenvref.current
      ?.instance()
      .getEditor("DBSRV_ABR")
      ?.option("value");

    const [dbalias, dbfile] = getAliasStrigsFromData(
      formDataSource?.PERSEED,
      formDataSource?.DBAUID
    );
    const isLocal = selectedSrv === "LOCAL";
    return (
      <div id={"radioNotes"} className={"notes"} ref={notes_db_ref}>
        <div id={"notes-fieldset"} className="dx-fieldset">
          <div className="dx-field">
            <div className={"dx-field-label"}>{"Stare"}</div>
            <LoadIndicator className="notes-indicator" />
            <div
              id={"notesDbStatus"}
              className={"dx-field-value"}
              ref={notes_db_status_ref}
            ></div>
          </div>
          <div className="dx-field">
            <div className={"dx-field-label"}>{"Srv:>"}</div>
            <div className={"dx-field-value-static"}>
              <code>{selectedSrv}</code>
            </div>
          </div>
          <div className="dx-field">
            <div className={"dx-field-label"}>{"Als:>"}</div>
            <div
              className={`dx-field-value-static ${
                isLocal ? "dx-state-disabled" : ""
              }`}
            >
              <code>{dbalias}</code>
            </div>
          </div>
          <div className="dx-field">
            <div className={"dx-field-label"}>{"Dbs:>"}</div>
            <div
              className={`dx-field-value-static ${
                isLocal ? "dx-state-disabled" : ""
              }`}
            >
              <code>{dbfile}</code>
            </div>
          </div>
        </div>
        <div className={"notes-buttons"}>
          <Switch value={dataCrc}>
            <Case when={[(x: string) => x === formDataCrc]}>
              <div className="default-placeholder">
                {"ACESTEA SUNT SETARILE CURENTE"}
              </div>
            </Case>
            <CaseElse>
              <Button
                className="notes-save-button"
                text={"Salvează"}
                icon={"save"}
                stylingMode="outlined"
                onClick={() => {
                  run(eUpdateDataset);
                }}
              />
              <Button
                className="notes-reset-button"
                text={"Revino"}
                icon={"refresh"}
                stylingMode="outlined"
                onClick={() => {
                  run(eLoadDataset);
                }}
              />
            </CaseElse>
          </Switch>
        </div>
      </div>
    );
  }, [formDataCrc]);

  const fieldDataChanged = async (e: any) => {
    const { dataField } = e || {};
    let { value } = e || {};
    if (!dataField) return;
    if (value === "") value = null;
    if (dataField === "DBSRV_ABR") {
      validateFbDatabaseAndShow({ ...currentEnvData, DBSRV_ABR: value });
    }
  };
  return (
    <div id={"su-environment-form"}>
      <Form
        ref={formorgenvref}
        id={"formOrgEnvData"}
        className={"form-form environment"}
        formData={formDataSource as ISUEnvironment0}
        showColonAfterLabel={true}
        labelLocation={"left"}
        colCount={1}
        width={"100%"}
        readOnly={readOnly}
        showValidationSummary={true}
        validationGroup="environmentGroupForm"
        onFieldDataChanged={fieldDataChanged}
      >
        <GroupItem caption={"Directorul de fisiere (kit)"} colCount={5}>
          <SimpleItem
            dataField={"KIT_SRV"}
            editorType={"dxTextBox"}
            editorOptions={kitSrvEditorOptions}
            colSpan={1}
          >
            <Label text={"Serverul de fisiere"}></Label>
          </SimpleItem>
          <SimpleItem render={notes_1} colSpan={4} />

          <SimpleItem render={notes_0} colSpan={5}>
            <Label visible={true} location={"left"} alignment={"center"}>
              <div>
                <i className="dx-icon dx-icon-tips"></i>
              </div>
            </Label>
          </SimpleItem>
        </GroupItem>
        <GroupItem
          caption={"Directorul de baza de date (Firebird)"}
          colCount={2}
        >
          <SimpleItem
            dataField={"DBSRV_ABR"}
            editorType={"dxRadioGroup"}
            editorOptions={radioDBServerOptions}
            colSpan={1}
          >
            <Label text={"Firebird"}></Label>
            <CustomRule
              message={"Nu se verifica dbs.conf sau calea catre baza de date "}
              validationCallback={() => formIsValid.current}
              ignoreEmptyValue={false}
            />
          </SimpleItem>
          <SimpleItem render={notes_db} colSpan={1} />
        </GroupItem>

        {/* <GroupItem caption={"Update de aplicatie"}>
          <SimpleItem
           avem asa variante: 

           ce inseamna local? verinipath. setuppath
           ce inseamna remote? verinipath. setuppath

           fist local | remote ; 
           second: local | remote;

          />
        </GroupItem> */}
        {/* <GroupItem caption={"Autentificare"}>
          <SimpleItem
            dataField={"DBPG_SRV"}
            editorType={'dxTextBox'}
            colSpan={1}
          >
            <Label text={"Metoda"}></Label>
          </SimpleItem>
        </GroupItem>
        <GroupItem caption={"Licentiere"}>
          <SimpleItem
            dataField={"DBPG_SRV"}
            editorType={'dxTextBox'}
            colSpan={1}
          >
            <Label text={"Metoda"}></Label>
          </SimpleItem>
          <SimpleItem
            editorType={'dxTextBox'}
          >
            <Label text={"Valabilitate"}></Label>
          </SimpleItem>
          <SimpleItem
            editorType={'dxTextBox'}
          >
            <Label text={"Numar licente"}></Label>
          </SimpleItem>
        </GroupItem>
        <GroupItem caption={"Utilizatori si drepturi de acces"}>
          <SimpleItem
            dataField={"DBPG_SRV"}
            editorType={'dxTextBox'}
            colSpan={1}
          >
            <Label text={"Modulul de definire"}></Label>
          </SimpleItem>
        </GroupItem> */}
      </Form>

      {/* <div>{"Db dirs"}</div>
      <div>{"JG_SWS Link"}</div>
      <div>{"Builder settings"}</div> */}
    </div>
  );
}
